import { useEffect, useState } from "react";
import { api, ArtifactExpiredError, type FrameEntry } from "../api";
import { ExpiredPlaceholder } from "./ExpiredPlaceholder";

interface Props {
  jobId: string;
}

type State =
  | { kind: "loading" }
  | { kind: "expired"; error: ArtifactExpiredError }
  | { kind: "error"; message: string }
  | { kind: "ready"; frames: FrameEntry[] };

export function FramesTab({ jobId }: Props) {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [active, setActive] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    api
      .artifactFrames(jobId)
      .then((view) => {
        if (cancelled) return;
        setState({ kind: "ready", frames: view.frames });
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ArtifactExpiredError) {
          setState({ kind: "expired", error: e });
        } else {
          setState({
            kind: "error",
            message: e instanceof Error ? e.message : String(e),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (state.kind === "loading") return <p className="muted">Loading frames…</p>;
  if (state.kind === "expired") return <ExpiredPlaceholder error={state.error} />;
  if (state.kind === "error") return <div className="error">{state.message}</div>;
  if (state.frames.length === 0)
    return <p className="muted">No frames were extracted.</p>;

  const focused = active !== null ? state.frames.find((f) => f.index === active) : null;
  return (
    <div>
      <div className="frames-grid">
        {state.frames.map((f) => (
          <button
            key={f.index}
            className="frame-cell"
            onClick={() => setActive(f.index === active ? null : f.index)}
          >
            <img src={f.signed_url} loading="lazy" alt={`frame ${f.index}`} />
            <div className="frame-cell-label">
              #{f.index}
              {f.captured_at_seconds !== null && (
                <span className="muted"> · {formatTs(f.captured_at_seconds)}</span>
              )}
            </div>
          </button>
        ))}
      </div>
      {focused && focused.annotation !== null && (
        <pre className="annotation-detail">{stringifyAnnotation(focused.annotation)}</pre>
      )}
    </div>
  );
}

function formatTs(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function stringifyAnnotation(value: unknown): string {
  if (value === null || value === undefined) return "(no annotation)";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
